// src/belediye/components/mesajlasma/BaslikAciklamaAdimi.js
// FAZ-83/A2 — BAŞLIK ve AÇIKLAMA TEK ADIMDA (tek kutu + seçim çipi).
//
// Kullanıcı kararı (2026-08-01): "başlık ve açıklama için alternatif 2'yi
// seçiyorum — tek kutu + seçim çipi." Kendi tarifi:
//   "ilk açılışta seçenek geliyor başlık ve açıklama. Ve mesaj kutusu başlık
//    için yer olur… ona başlık yazıldıktan sonra ikinci olarak açıklama olur."
//
// FAZ-83/2 (aynı gün, ikinci tur): "çip mantığı güzel ama burada açıklama için
// ANA MESAJ KUTUSUNU kullanalım, yeniden bir alan oluşturma." → Kutu artık
// adımın kendi kutusu değil, sihirbazın ALT SATIRININ ta kendisi. Ekranda tek
// giriş yüzeyi var; çipler yalnız o yüzeyin hangi alanı yazdığını söyler.
//
// A2'nin bilinen zayıf yanı GİZLİ DURUM idi: çipe basmayan kullanıcı açıklama
// alanının varlığını fark etmeyebilir. İKİ önlem var:
//   1) Başlıkta Enter → çip kendiliğinden Açıklama'ya döner (tek tık bile yok).
//   2) Yazı yazılıp ötekine hiç geçilmediyse öteki çipten DALGA çıkar.
//      FAZ-83/3'te kesikli çerçeve + nokta denenmişti; FAZ-83/4'te kullanıcı
//      geri istedi: "o çipten dalga gibi yayılarak çıkıyordu ya, onu
//      istiyorum; onun dışındakiler çok karışık."
//
// Sözleşme: onDone({ title, description }) — ikisi de boş olabilir; boş başlık
// TaskComposer'da açıklamanın ilk satırından türetilir (eski davranış).
import React, { useEffect, useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

import SihirbazAltSatir from "./SihirbazAltSatir";

// FAZ-83/3 (kullanıcı, 2026-08-01): "mesaj kutusundaki gri yönlendirici yazı
// GERÇEK BİR YAZIYMIŞ GİBİ mesaj kutusunu aşağı sürüklüyor… buradaki yazı çok
// önemli değil, tasarımı bozmasın." Uzun yer tutucu kutuda sarıyor, otomatik
// büyüme onu içerik sanıp kutuyu iki satıra çıkarıyordu. Metinler artık TEK
// SATIRA sığacak kadar kısa (uzun/kısa ayrımına da gerek kalmadı) ve boş
// kutuda otomatik büyüme hiç ölçüm yapmıyor (aşağıdaki useEffect).
const ALANLAR = [
  { key: "title", ad: "Başlık", ph: "Başlık…", max: 200 },
  { key: "description", ad: "Açıklama", ph: "Açıklama…", max: 2000 },
];

export default function BaslikAciklamaAdimi({
  initialTitle = "",
  initialDescription = "",
  onDone,
  onSkip,
  pasif = false,
}) {
  // Açılışta BAŞLIK çipi seçili — kullanıcının tarifindeki sıra.
  const [alan, setAlan] = useState("title");
  const [baslik, setBaslik] = useState(initialTitle);
  const [aciklama, setAciklama] = useState(initialDescription);
  const kutuRef = useRef(null);

  const etkin = ALANLAR.find((a) => a.key === alan) || ALANLAR[0];
  const deger = alan === "title" ? baslik : aciklama;
  const yaz = alan === "title" ? setBaslik : setAciklama;
  const doluMu = (k) => (k === "title" ? baslik : aciklama).trim().length > 0;
  // Etkin alan doldu ama ötekine hiç geçilmediyse öteki çipten dalga çıkar.
  const parlaMi = (k) => k !== alan && doluMu(alan) && !doluMu(k);

  // Kutu WhatsApp-stili yazdıkça büyür (108 = CSS max-height ile aynı).
  // BOŞKEN HİÇ ÖLÇÜLMEZ: `scrollHeight` yer tutucu metnini de sayıyor, uzun
  // bir yer tutucu sarınca kutu yazı yokken bile iki satıra çıkıyordu
  // (FAZ-83/3 şikâyeti). Boş kutu doğal tek satır yüksekliğinde kalır.
  useEffect(() => {
    const el = kutuRef.current;
    if (!el) return;
    if (!deger) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 108)}px`;
  }, [deger, alan]);

  // Çip değişince odak kutuya döner — kullanıcı çipe basıp klavyeyi yeniden
  // aramak zorunda kalmasın (mobilde klavye hiç kapanmaz).
  useEffect(() => {
    const el = kutuRef.current;
    if (el && !pasif) el.focus();
  }, [alan, pasif]);

  const gonder = () => {
    if (pasif) return;
    if (!baslik.trim() && !aciklama.trim()) return;
    onDone({ title: baslik.trim(), description: aciklama.trim() });
  };

  // Enter: başlıkta ilerlet (açıklamaya geç), açıklamada gönder.
  // Shift+Enter her iki alanda da satır atlar.
  const enter = () => {
    if (alan === "title") {
      setAlan("description");
      return;
    }
    gonder();
  };

  return (
    <div className="msj-ikili">
      <div className="msj-secim-cipler" role="tablist" aria-label="Doldurulacak alan">
        {ALANLAR.map((a) => (
          <button
            key={a.key}
            type="button"
            role="tab"
            aria-selected={alan === a.key}
            title={`${a.ad} yaz — mesaj kutusu bu alanı kaydeder`}
            className={`msj-due-cip msj-secim-cip ${alan === a.key ? "is-acik" : ""} ${
              doluMu(a.key) ? "is-dolu" : ""
            } ${parlaMi(a.key) ? "is-parla" : ""}`}
            disabled={pasif}
            onClick={() => setAlan(a.key)}
          >
            {a.ad}
            {doluMu(a.key) && <CheckIcon className="msj-secim-tik" />}
          </button>
        ))}
        <span className="msj-secim-ipucu">ikisini de doldurabilirsiniz</span>
      </div>

      {/* Giriş yüzeyi ADIMIN KENDİ kutusu değil — sihirbazın ana mesaj
          kutusunun ta kendisi (FAZ-83/2). */}
      <SihirbazAltSatir
        onSkip={onSkip}
        skipTitle="Başlık ve açıklama yazmadan devam et"
        gonderAktif={!!(baslik.trim() || aciklama.trim())}
        onGonder={gonder}
        gonderTitle="Devam et"
        pasif={pasif}
      >
        <textarea
          ref={kutuRef}
          rows={1}
          value={deger}
          maxLength={etkin.max}
          placeholder={etkin.ph}
          aria-label={etkin.ad}
          autoFocus
          disabled={pasif}
          onChange={(e) => yaz(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              enter();
            }
          }}
        />
      </SihirbazAltSatir>
    </div>
  );
}
